/**
 * 画布内提示词库选择弹窗
 *
 * 复用已有的 templateAPI，选择模板后将 templateText 填入生成节点的提示词框。
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Loader2, BookOpen } from 'lucide-react';
import { templateAPI, type PromptTemplate, type PromptTemplateDetail } from '../../services/api';

interface PromptLibraryModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (prompt: string) => void;
}

export function PromptLibraryModal({ open, onClose, onSelect }: PromptLibraryModalProps) {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PromptTemplateDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 加载模板列表
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    templateAPI
      .getTemplates({ search: search || undefined })
      .then((res) => setTemplates(res.data))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, search]);

  // 选择模板时加载详情
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    templateAPI
      .getTemplate(selectedId)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  if (!open) return null;

  const handleUse = () => {
    const text = detail?.templateText || detail?.examplePrompt || '';
    if (text) {
      onSelect(text);
    }
    onClose();
  };

  // portal 到 body：本组件渲染在生图节点的 NodeWrapper 内，位于画布
  // transform 视口之下，fixed 定位会以画布世界原点为基准错位（项目内
  // ContextMenu / NodeInfoDialog 等均按此规范 portal）
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex h-[520px] w-[680px] max-w-[90vw] overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 左侧：模板列表 */}
        <div className="flex w-[260px] flex-col border-r border-slate-100">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-900">提示词库</h3>
            <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-3 py-2">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜索模板..."
                className="flex-1 bg-transparent text-xs outline-none placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : templates.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-400">暂无模板</div>
            ) : (
              templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedId(t.id)}
                  className={`mb-1 w-full rounded-lg px-3 py-2.5 text-left transition ${
                    selectedId === t.id
                      ? 'bg-blue-50 ring-1 ring-blue-200'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="text-sm font-medium text-slate-800 line-clamp-1">{t.title}</div>
                  {t.description && (
                    <div className="mt-0.5 text-xs text-slate-400 line-clamp-1">{t.description}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* 右侧：模板详情 */}
        <div className="flex flex-1 flex-col">
          {selectedId && detailLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : detail ? (
            <>
              <div className="border-b border-slate-100 px-5 py-4">
                <h3 className="text-base font-semibold text-slate-900">{detail.title}</h3>
                {detail.description && (
                  <p className="mt-1 text-xs text-slate-500">{detail.description}</p>
                )}
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <label className="mb-1.5 block text-xs font-medium text-slate-500">模板内容</label>
                <pre className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
                  {detail.templateText || detail.examplePrompt || '（空模板）'}
                </pre>
                {detail.examplePrompt && detail.templateText && (
                  <>
                    <label className="mb-1.5 mt-4 block text-xs font-medium text-slate-500">示例提示词</label>
                    <pre className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">
                      {detail.examplePrompt}
                    </pre>
                  </>
                )}
              </div>
              <div className="flex justify-end border-t border-slate-100 px-5 py-3">
                <button
                  type="button"
                  onClick={handleUse}
                  disabled={!detail.templateText && !detail.examplePrompt}
                  className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  使用此模板
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-slate-400">
              <BookOpen className="h-8 w-8" />
              <span className="text-sm">选择左侧模板查看详情</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
